import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { reportsAPI, employeeAPI } from '../services/api';
import { FileText, Users, Calendar, DollarSign, Download, Filter, RefreshCw, Clock, ArrowUpDown, BarChart3, Inbox } from 'lucide-react';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from '../components/ui/table-controls';
import { TableExportActions } from '../components/ui/table-export-actions';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const ReportsHub = ({ currentTab = 'employee', viewMode = 'admin' }) => {
  const isEmployeeView = viewMode === 'employee';
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [employeeFilters, setEmployeeFilters] = useState({
    department: '',
    sub_department: '',
    designation: '',
    status: '',
    joining_from: '',
    joining_to: ''
  });
  
  const [attendanceFilters, setAttendanceFilters] = useState({
    employee_id: '',
    department: '',
    sub_department: '',
    from_date: '',
    to_date: '',
    status: ''
  });

  const [musterFilters, setMusterFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employee_id: '',
    department: '',
    sub_department: '',
    approval_status: ''
  });
  
  const [leaveFilters, setLeaveFilters] = useState({
    employee_id: '',
    department: '',
    sub_department: '',
    leave_type: '',
    status: '',
    from_date: '',
    to_date: ''
  });
  
  const [payrollFilters, setPayrollFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    department: '',
    sub_department: '',
  });

  const [statutoryFilters, setStatutoryFilters] = useState({
    financial_year: new Date().getMonth() + 1 >= 4
      ? `${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(-2)}`
      : `${new Date().getFullYear() - 1}-${String(new Date().getFullYear()).slice(-2)}`,
    department: '',
    sub_department: '',
    active_only: 'true'
  });

  const [performanceFilters, setPerformanceFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    department: '',
    sub_department: '',
  });

  const fetchInitialData = useCallback(async () => {
    try {
      if (isEmployeeView) {
        return;
      }
      const res = await employeeAPI.list();
      setEmployees(res.data);
      // Extract unique departments
      const depts = [...new Set(res.data.map(e => e.department).filter(Boolean))];
      setDepartments(depts);
      const subDepts = [...new Set(res.data.map(e => e.sub_department).filter(Boolean))];
      setSubDepartments(subDepts);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [isEmployeeView]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const generateReport = async (reportType) => {
    setLoading(true);
    setReportData(null);
    
    try {
      let result;
      switch (reportType) {
        case 'employee':
          result = await reportsAPI.employeeReport(employeeFilters);
          break;
        case 'attendance':
          result = await reportsAPI.attendanceReport(attendanceFilters);
          break;
        case 'muster':
          result = await reportsAPI.musterRegister(musterFilters);
          break;
        case 'leave':
          result = await reportsAPI.leaveReport(leaveFilters);
          break;
        case 'payroll':
          result = await reportsAPI.payrollReport(payrollFilters);
          break;
        case 'leave_balance':
          result = await reportsAPI.leaveBalanceReport({
            department: leaveFilters.department,
            sub_department: leaveFilters.sub_department,
          });
          break;
        case 'statutory_compliance':
          result = await reportsAPI.statutoryComplianceReport({
            ...statutoryFilters,
            active_only: statutoryFilters.active_only === 'true'
          });
          break;
        case 'performance':
          result = isEmployeeView
            ? await reportsAPI.myPerformanceReport({
              month: performanceFilters.month,
              year: performanceFilters.year,
            })
            : await reportsAPI.performanceReport(performanceFilters);
          break;
        default:
          throw new Error('Invalid report type');
      }
      setReportData(result.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate report'));
    } finally {
      setLoading(false);
    }
  };

  const exportPerformance = async (format) => {
    try {
      const response = isEmployeeView
        ? await reportsAPI.exportMyPerformanceReport({
          month: performanceFilters.month,
          year: performanceFilters.year,
          format,
        })
        : await reportsAPI.exportPerformanceReport({
          ...performanceFilters,
          format,
        });
      const blob = new Blob([response.data], {
        type:
          format === 'pdf'
            ? 'application/pdf'
            : format === 'excel'
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : 'text/csv',
      });
      const ext = format === 'excel' ? 'xlsx' : format;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance_report_${performanceFilters.year}_${String(performanceFilters.month).padStart(2, '0')}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Performance report exported (${format.toUpperCase()})`);
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to export ${format.toUpperCase()}`));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const financialYears = ['2025-26', '2026-27', '2027-28'];
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  }, []);

  const reportRows = useMemo(() => {
    return Array.isArray(reportData?.data) ? reportData.data : [];
  }, [reportData]);

  const reportColumns = useMemo(() => {
    return reportRows[0] ? Object.keys(reportRows[0]) : [];
  }, [reportRows]);

  const reportExportColumns = useMemo(
    () => reportColumns.map((key) => ({ label: key.replace(/_/g, ' '), accessor: (row) => row[key] })),
    [reportColumns]
  );

  const reportTable = useTableControls(reportRows);

  const renderSortableHeader = (key) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-semibold text-muted-foreground capitalize hover:text-primary"
      onClick={() => reportTable.onSort(key)}
    >
      {key.replace(/_/g, ' ')}
      <ArrowUpDown className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-hub">
        {/* Employee Report Tab */}
        {currentTab === 'employee' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <Users className="h-5 w-5 text-primary" />
                Employee Report Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Department</Label>
                  <Select
                    value={employeeFilters.department}
                    onValueChange={(v) => setEmployeeFilters({...employeeFilters, department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={employeeFilters.sub_department}
                    onValueChange={(v) => setEmployeeFilters({...employeeFilters, sub_department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Status</Label>
                  <Select
                    value={employeeFilters.status}
                    onValueChange={(v) => setEmployeeFilters({...employeeFilters, status: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Joining From</Label>
                  <Input
                    type="date"
                    value={employeeFilters.joining_from}
                    onChange={(e) => setEmployeeFilters({...employeeFilters, joining_from: e.target.value})}
                    className="bg-background border-border focus:bg-background transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Joining To</Label>
                  <Input
                    type="date"
                    value={employeeFilters.joining_to}
                    onChange={(e) => setEmployeeFilters({...employeeFilters, joining_to: e.target.value})}
                    className="bg-background border-border focus:bg-background transition-colors"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => generateReport('employee')} 
                    disabled={loading}
                    data-testid="generate-employee-report"
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendance Report Tab */}
        {currentTab === 'attendance' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <Clock className="h-5 w-5 text-primary" />
                Attendance Report Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Employee</Label>
                  <Select
                    value={attendanceFilters.employee_id}
                    onValueChange={(v) => setAttendanceFilters({...attendanceFilters, employee_id: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Employees</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={attendanceFilters.sub_department}
                    onValueChange={(v) => setAttendanceFilters({...attendanceFilters, sub_department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">From Date</Label>
                  <Input
                    type="date"
                    value={attendanceFilters.from_date}
                    onChange={(e) => setAttendanceFilters({...attendanceFilters, from_date: e.target.value})}
                    className="bg-background border-border focus:bg-background transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">To Date</Label>
                  <Input
                    type="date"
                    value={attendanceFilters.to_date}
                    onChange={(e) => setAttendanceFilters({...attendanceFilters, to_date: e.target.value})}
                    className="bg-background border-border focus:bg-background transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Status</Label>
                  <Select
                    value={attendanceFilters.status}
                    onValueChange={(v) => setAttendanceFilters({...attendanceFilters, status: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Status</SelectItem>
                      <SelectItem value="present">Present</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => generateReport('attendance')} 
                    disabled={loading}
                    data-testid="generate-attendance-report"
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {currentTab === 'muster' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <Calendar className="h-5 w-5 text-primary" />
                Muster Register Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Month</Label>
                  <Select
                    value={musterFilters.month.toString()}
                    onValueChange={(v) => setMusterFilters({ ...musterFilters, month: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((m, i) => (
                        <SelectItem key={m} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Year</Label>
                  <Select
                    value={musterFilters.year.toString()}
                    onValueChange={(v) => setMusterFilters({ ...musterFilters, year: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Employee</Label>
                  <Select
                    value={musterFilters.employee_id}
                    onValueChange={(v) => setMusterFilters({ ...musterFilters, employee_id: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Employees</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Department</Label>
                  <Select
                    value={musterFilters.department}
                    onValueChange={(v) => setMusterFilters({ ...musterFilters, department: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={musterFilters.sub_department}
                    onValueChange={(v) => setMusterFilters({ ...musterFilters, sub_department: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Approval Status</Label>
                  <Select
                    value={musterFilters.approval_status}
                    onValueChange={(v) => setMusterFilters({ ...musterFilters, approval_status: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => generateReport('muster')}
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leave Report Tab */}
        {currentTab === 'leave' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
            <CardTitle className="flex items-center gap-2 text-base text-foreground">
              <Calendar className="h-5 w-5 text-primary" />
              Leave Report Filters
            </CardTitle>
          </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Employee</Label>
                  <Select
                    value={leaveFilters.employee_id}
                    onValueChange={(v) => setLeaveFilters({...leaveFilters, employee_id: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Employees</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={leaveFilters.sub_department}
                    onValueChange={(v) => setLeaveFilters({...leaveFilters, sub_department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Leave Type</Label>
                  <Select
                    value={leaveFilters.leave_type}
                    onValueChange={(v) => setLeaveFilters({...leaveFilters, leave_type: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Types</SelectItem>
                      <SelectItem value="sick">Sick</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Status</Label>
                  <Select
                    value={leaveFilters.status}
                    onValueChange={(v) => setLeaveFilters({...leaveFilters, status: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">From Date</Label>
                  <Input
                    type="date"
                    value={leaveFilters.from_date}
                    onChange={(e) => setLeaveFilters({...leaveFilters, from_date: e.target.value})}
                    className="bg-background border-border focus:bg-background transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">To Date</Label>
                  <Input
                    type="date"
                    value={leaveFilters.to_date}
                    onChange={(e) => setLeaveFilters({...leaveFilters, to_date: e.target.value})}
                    className="bg-background border-border focus:bg-background transition-colors"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => generateReport('leave')} 
                    disabled={loading}
                    data-testid="generate-leave-report"
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payroll Report Tab */}
        {currentTab === 'payroll' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <DollarSign className="h-5 w-5 text-primary" />
                Payroll Report Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Month</Label>
                  <Select
                    value={payrollFilters.month.toString()}
                    onValueChange={(v) => setPayrollFilters({...payrollFilters, month: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((m, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Year</Label>
                  <Select
                    value={payrollFilters.year.toString()}
                    onValueChange={(v) => setPayrollFilters({...payrollFilters, year: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Department</Label>
                  <Select
                    value={payrollFilters.department}
                    onValueChange={(v) => setPayrollFilters({...payrollFilters, department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={payrollFilters.sub_department}
                    onValueChange={(v) => setPayrollFilters({...payrollFilters, sub_department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => generateReport('payroll')} 
                    disabled={loading}
                    data-testid="generate-payroll-report"
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {currentTab === 'performance' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <BarChart3 className="h-5 w-5 text-primary" />
                Performance Report Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Month</Label>
                  <Select
                    value={performanceFilters.month.toString()}
                    onValueChange={(v) => setPerformanceFilters({ ...performanceFilters, month: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((m, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Year</Label>
                  <Select
                    value={performanceFilters.year.toString()}
                    onValueChange={(v) => setPerformanceFilters({ ...performanceFilters, year: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!isEmployeeView && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Department</Label>
                    <Select
                      value={performanceFilters.department}
                      onValueChange={(v) => setPerformanceFilters({ ...performanceFilters, department: v })}
                    >
                      <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All Departments</SelectItem>
                        {departments.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!isEmployeeView && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Sub Department</Label>
                    <Select
                      value={performanceFilters.sub_department}
                      onValueChange={(v) => setPerformanceFilters({ ...performanceFilters, sub_department: v })}
                    >
                      <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All Sub Departments</SelectItem>
                        {subDepartments.map(sd => (
                          <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-end">
                  <Button
                    onClick={() => generateReport('performance')}
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {currentTab === 'statutory_compliance' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <FileText className="h-5 w-5 text-primary" />
                Statutory Compliance Report
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Financial Year</Label>
                  <Select
                    value={statutoryFilters.financial_year}
                    onValueChange={(v) => setStatutoryFilters({ ...statutoryFilters, financial_year: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {financialYears.map((fy) => (
                        <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Department</Label>
                  <Select
                    value={statutoryFilters.department}
                    onValueChange={(v) => setStatutoryFilters({ ...statutoryFilters, department: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={statutoryFilters.sub_department}
                    onValueChange={(v) => setStatutoryFilters({ ...statutoryFilters, sub_department: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Employees</Label>
                  <Select
                    value={statutoryFilters.active_only}
                    onValueChange={(v) => setStatutoryFilters({ ...statutoryFilters, active_only: v })}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active Only</SelectItem>
                      <SelectItem value="false">All Employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => generateReport('statutory_compliance')}
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leave Balance Report Tab */}
        {currentTab === 'leave_balance' && (
          <Card className="sticky top-2 z-20 hover-lift border shadow-sm bg-background">
            <CardHeader className="bg-muted/50 border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-foreground">
                <Filter className="h-5 w-5 text-primary" />
                Leave Balance Report
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Department</Label>
                  <Select
                    value={leaveFilters.department}
                    onValueChange={(v) => setLeaveFilters({...leaveFilters, department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Sub Department</Label>
                  <Select
                    value={leaveFilters.sub_department}
                    onValueChange={(v) => setLeaveFilters({...leaveFilters, sub_department: v})}
                  >
                    <SelectTrigger className="bg-background border-border focus:bg-background transition-colors">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Sub Departments</SelectItem>
                      {subDepartments.map(sd => (
                        <SelectItem key={sd} value={sd}>{sd}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => generateReport('leave_balance')} 
                    disabled={loading}
                    data-testid="generate-balance-report"
                    className="w-full bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Loading skeleton */}
      {loading && !reportData && (
        <CardSkeleton rows={6} />
      )}

      {/* Report Results */}
      {reportData && (
        <Card className="hover-lift shadow-sm border animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <CardHeader className="bg-muted/50 border-b pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="capitalize text-lg text-foreground flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                {reportData.report_type?.replace('_', ' ')} Report
              </CardTitle>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-card border-border">
                  Generated: {new Date(reportData.generated_at).toLocaleString()}
                </Badge>
                {currentTab === 'performance' ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => exportPerformance('csv')} className="h-8 text-xs border-primary/20 text-primary hover:bg-primary/10">
                      <Download className="h-3 w-3 mr-1" />
                      CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => exportPerformance('excel')} className="h-8 text-xs border-primary/20 text-primary hover:bg-primary/10">
                      <Download className="h-3 w-3 mr-1" />
                      Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => exportPerformance('pdf')} className="h-8 text-xs border-primary/20 text-primary hover:bg-primary/10">
                      <Download className="h-3 w-3 mr-1" />
                      PDF
                    </Button>
                  </>
                ) : (
                  <TableExportActions
                    rows={reportRows}
                    columns={reportExportColumns}
                    fileName={`${reportData.report_type || 'report'}_${new Date().toISOString().split('T')[0]}`}
                    title={`${reportData.report_type?.replace('_', ' ') || 'Report'} Export`}
                  />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Summary */}
            {reportData.summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {Object.entries(reportData.summary).map(([key, value]) => {
                  const isCurrency = typeof value === 'number' && key.includes('total') && !key.includes('count') && !key.includes('employees');
                  if (typeof value === 'number' && !isCurrency) {
                    return (
                      <StatCard
                        key={key}
                        label={key.replace(/_/g, ' ')}
                        value={value}
                        icon={BarChart3}
                        tone="primary"
                        className="capitalize"
                      />
                    );
                  }
                  return (
                    <div key={key} className="p-4 bg-card border border-border/50 rounded-lg shadow-sm hover:shadow transition-all text-center animate-fade-in">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-xl font-bold text-foreground">
                        {isCurrency ? formatCurrency(value) : value}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Data Table */}
            <div className="overflow-x-auto border rounded-lg shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b border-border">
                    {reportColumns.map(key => (
                      <th key={key} className="text-left p-3">
                        {renderSortableHeader(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {reportTable.data.map((row, idx) => (
                    <tr key={idx} className="hover:bg-muted/50 transition-colors">
                      {Object.entries(row).map(([key, value], i) => (
                        <td key={i} className="p-3 text-foreground">
                          {[
                            'gross_earnings',
                            'net_salary',
                            'total_deductions',
                            'pf_employee',
                            'pf_employer',
                            'pf_expected_employee',
                            'pf_expected_employer',
                            'esi_employee',
                            'esi_employer',
                            'esi_expected_employee',
                            'esi_expected_employer',
                          ].includes(key) || key.includes('amount')
                            ? formatCurrency(value)
                            : value?.toString() || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {reportData.data?.length === 0 && (
                <div className="bg-muted/30">
                  <EmptyState
                    icon={Inbox}
                    title="No data found"
                    description="No records match the selected filters. Try adjusting your criteria and generate again."
                  />
                </div>
              )}
            </div>
            {reportRows.length > 0 && (
              <TableControls
                totalRows={reportTable.totalRows}
                page={reportTable.page}
                totalPages={reportTable.totalPages}
                pageSize={reportTable.pageSize}
                pageSizes={reportTable.pageSizes}
                onPageSizeChange={reportTable.onPageSizeChange}
                onPrevious={reportTable.goToPreviousPage}
                onNext={reportTable.goToNextPage}
              />
            )}
            <p className="text-xs text-muted-foreground mt-4 flex items-center justify-end">
              Total Records: <span className="font-medium ml-1">{reportData.total_count || reportRows.length || 0}</span>
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsHub;
